<template>
	<div class="team">
		<div class="introduce-box panel-light">
			<div class="icon-box">
				<div class="icon">
					<div
						class="head"
						:style="{
							'background-image': `url(${
								teamInfo.avatar ? teamInfo.avatar : ''
							})`,
						}"
					></div>
					<div class="frame"></div>
				</div>
			</div>
			<div class="intro-box">
				<div class="introduce">
					<div class="header wrap">
						<div class="title">
							<div class="label-box">
								<span class="label-shadow">{{ teamInfo.name ? teamInfo.name : "-" }}</span>
								<span class="label-text">{{ teamInfo.name ? teamInfo.name : "-" }}</span>
							</div>
						</div>
						<UiButton
							class="focus-box"
							:class="{ focus: !teamInfo.isFollowed, unfocus: teamInfo.isFollowed }"
							@click.stop="handleFocus(teamInfo.teamCode, !teamInfo.isFollowed)"
						>
							<i></i>
							<div class="label-box">
								<span class="label-shadow">{{ teamInfo.isFollowed ? '已关注' : '关注' }}</span>
								<span class="label-text">{{ teamInfo.isFollowed ? '已关注' : '关注' }}</span>
							</div>
						</UiButton>
					</div>
					<div class="des wrap">
						{{ teamInfo.desc ? teamInfo.desc : default_introduce }}
					</div>
					<ul class="rank wrap">
						<li class="info-rank">
							<i></i>
							<div class="label-box">
								<span class="label-shadow">赛季排名</span>
								<span class="label-text">赛季排名</span>
							</div>
							<div class="label-box num">
								<span class="label-shadow">{{ teamInfo.rank ? teamInfo.rank : '-' }}</span>
								<span class="label-text">{{ teamInfo.rank ? teamInfo.rank : '-' }}</span>
							</div>
						</li>
						<li class="info-popularity">
							<i></i>
							<div class="label-box">
								<span class="label-shadow">人气值</span>
								<span class="label-text">人气值</span>
							</div>
							<div class="label-box num">
								<span class="label-shadow">{{ popularityDisplay.value }}</span>
								<span class="label-text">{{ popularityDisplay.value }}</span>
							</div>
							<div v-if="popularityDisplay.unit" class="label-box">
								<span class="label-shadow">{{ popularityDisplay.unit }}</span>
								<span class="label-text">{{ popularityDisplay.unit }}</span>
							</div>
						</li>
						<li class="info-follow">
							<i></i>
							<div class="label-box">
								<span class="label-shadow">战队关注数</span>
								<span class="label-text">战队关注数</span>
							</div>
							<div class="label-box num">
								<span class="label-shadow">{{ followNumDisplay.value }}</span>
								<span class="label-text">{{ followNumDisplay.value }}</span>
							</div>
							<div v-if="followNumDisplay.unit" class="label-box">
								<span class="label-shadow">{{ followNumDisplay.unit }}</span>
								<span class="label-text">{{ followNumDisplay.unit }}</span>
							</div>
						</li>
					</ul>
				</div>
			</div>
		</div>
		<div class="data-box">
			<div class="base-data panel-light">
				<div class="title-box">
					<div class="title">
						<div class="label-box">
							<span class="label-shadow">赛事数据</span>
							<span class="label-text">赛事数据</span>
						</div>
					</div>
					<div class="switch-source-slot">
						<UiSelect :list="teamHistoryList" :selected-id="subSeasonID" @switch="handleSwitch" />
					</div>
				</div>
				<div v-show="showBaseData" class="content" ref="baseData"></div>
				<UiEmpty v-show="!showBaseData" tip="暂无数据，首场比赛后即可查看" />
			</div>
			<!-- 历史荣耀 -->
			<div class="history-reward panel-light">
				<div class="title-box">
					<div class="title">
						<div class="label-box">
							<span class="label-shadow">历史荣耀</span>
							<span class="label-text">历史荣耀</span>
						</div>
					</div>
				</div>
				<div
					class="content"
					v-if="historySlides.length !== 0"
				>
					<div class="swiper history-swiper" ref="historySwiper">
						<div class="swiper-wrapper">
							<div
								class="swiper-slide"
								v-for="(glory, key) in historySlides"
								:key="key"
							>
								<div
									class="history-slide"
									:style="getHistorySlideStyle(glory)"
								></div>
							</div>
						</div>
					</div>
					<button
						type="button"
						class="history-button history-button-prev"
						ref="historyPrev"
					></button>
					<button
						type="button"
						class="history-button history-button-next"
						ref="historyNext"
					></button>
				</div>
				<UiEmpty v-else tip="战队正在努力夺冠中~" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import UiSelect from "@/components/ui-select";
import * as api from "@/service/index";
import * as echarts from "echarts";
import Swiper from "swiper";
import { Navigation } from "swiper/modules";
import "swiper/css";
import { useAppStore } from '@/store'
import { usePrompt } from '@/composables/usePrompt';
import { getOption, updataOption } from "@/utils/initEcharts";
import UiButton from "@/components/ui-button";
import UiEmpty from "@/components/ui-empty";

const props = defineProps({
	subTeam: {
		type: Object,
		default: () => {},
	},
});

const route = useRoute();
const appStore = useAppStore();

const teamInfo = ref({});

// 大数值拆成 "1.23" + "万" 两段, 让"万"跟着"人气值/关注数"标签样式走
function splitCount(raw, { fallback = 0 } = {}) {
  if (raw == null || raw === '') return { value: fallback, unit: '' }
  const num = Number(raw)
  if (!Number.isFinite(num)) return { value: fallback, unit: '' }
  if (num < 10000) return { value: num, unit: '' }
  if (num < 99999999) return { value: (num / 10000).toFixed(2), unit: '万' }
  return { value: 9999, unit: '万+' }
}
const popularityDisplay = computed(() => splitCount(teamInfo.value?.popularity, { fallback: 0 }))
const followNumDisplay = computed(() => {
  const n = teamInfo.value?.followNum
  if (n == null) return { value: '-', unit: '' }
  return splitCount(n, { fallback: '-' })
})
const subSeasonID = ref('');
const teamHistoryList = ref([]);
const default_introduce = ref("这个战队，什么话都没留下～ ");
const option = ref({});
const myChart = ref(null);
const inRequire = ref(false);
const showBaseData = ref(false);
const historySwiperInstance = ref(null);

// 模板 refs
const baseData = ref(null);
const historySwiper = ref(null);
const historyPrev = ref(null);
const historyNext = ref(null);
const { showPrompt } = usePrompt();

const token = computed(() => appStore.token);
const seasonInfo = computed(() => appStore.seasonInfo);
const isOnChange = computed(() => {
	return { seasonInfo: seasonInfo.value, subTeam: props.subTeam }
});
const historySlides = computed(() => {
	const history = Array.isArray(teamInfo.value.history)
		? [...teamInfo.value.history]
		: [];
	if (history.length % 2 !== 0) {
		history.push("");
	}
	return history;
});

watch(
	isOnChange,
	(newVal) => {
		if(newVal && Object.keys(newVal).length >= 2) {
			let seasonInfo = newVal.seasonInfo;
			let subTeam = newVal.subTeam;

			if(Object.keys(seasonInfo).length !== 0 && Object.keys(subTeam).length !== 0) {
				subSeasonID.value = seasonInfo.id;
				getMembersInfo(subTeam);
			}
		}
	},
	{
		immediate: true,
		deep: true
	}
);

function getMembersInfo(data = {}) {
	let subTeam = route.params.subTeam;
	if (subTeam) {
		if (Object.keys(subTeam).length !== 0) {
			getTeamInfo(subTeam);
		}
	} else {
		if (Object.keys(data).length !== 0) {
			getTeamInfo(data);
		}
	}
}

function getTeamInfo(subTeam) {
	teamInfo.value = subTeam;
	let historyList = teamInfo.value.teamHistoryList;
	// 默认显示最近赛季的数据
	if (historyList && historyList.length !== 0) {
		historyList.forEach((match) => {
			match.id = match.seasonId;
			match.name = match.seasonName;
		});
		teamHistoryList.value = historyList;
	}else {
		teamHistoryList.value = [];
	}
	nextTick(() => {
		// 数据更新后，重建历史荣耀轮播并刷新当前赛季图表
		initHistorySwiper();
		// 渲染赛季数据
		let data = teamInfo.value.matchData || {};
		// console.log('初始化数据',data);
		showData(data, () => {
			initEcharts();
			updataEcharts(data)
		});
	})

	// 埋点
	if(typeof tapdb === 'undefined') return;
	tapdb.trackEvent('战队详情', {
		'#team_id': teamInfo.value.name
	});
}

function initEcharts() {
	let chartDom = baseData.value;
	myChart.value = echarts.init(chartDom);
	option.value = getOption(chartDom, 'team');
	option.value && myChart.value.setOption(option.value);
	chartDom.childNodes[0].style.position = "absolute";
}

function updataEcharts(subSeason) {
	option.value = updataOption(subSeason, option.value, "team");
	option.value && myChart.value.setOption(option.value);
}

function handleSwitch (seasonId) {
	if(seasonId === subSeasonID.value) return;
	subSeasonID.value = seasonId
	const subSeason = teamHistoryList.value.find((item) => item.seasonId === seasonId);
	if(!token.value) return;
	let datas = {
		token: token.value,
		seasonId: seasonId,
		teamCode: subSeason.teamCode
	}
	api.reqTeamInfo(datas).then((res)=>{
		const { data } = res
		if(data.matchData){
			showData(data.matchData, () => {
				initEcharts();
				updataEcharts(data.matchData)
			});
		}
	}).catch((err)=>{
		console.log(err);
	});
}

function showData(matchData, callback) {

	if (
		matchData.maxAvgKill &&
		matchData.maxAvgAccuracy &&
		matchData.maxAvgSurviveTime &&
		matchData.maxAvgTravelDistance &&
		matchData.maxAvgDamage
	) {
		// 该战队数据为0时，不显示五边形图
		if (
			(!matchData.teamAvgAccuracy ||
				matchData.teamAvgAccuracy === 0 ||
				matchData.teamAvgAccuracy === "0.00") &&
			(!matchData.teamAvgDamage ||
				matchData.teamAvgDamage === 0 ||
				matchData.teamAvgDamage === "0.00") &&
			(!matchData.teamAvgKill ||
				matchData.teamAvgKill === 0 ||
				matchData.teamAvgKill === "0.00") &&
			(!matchData.teamAvgSurviveTime ||
				matchData.teamAvgSurviveTime === 0 ||
				matchData.teamAvgSurviveTime === "0.00") &&
			(!matchData.teamAvgTravelDistance ||
				matchData.teamAvgTravelDistance === 0 ||
				matchData.teamAvgTravelDistance === "0.00")
		) {
			showBaseData.value = false;
		} else {
			showBaseData.value = true;
			setTimeout(() => {
				callback();
			}, 0)
		}
	}else {
		showBaseData.value = false;
	}
	// console.log('object',showBaseData.value);
}

function initHistorySwiper() {
	destroyHistorySwiper();
	if (!historySwiper.value) return;

	historySwiperInstance.value = new Swiper(historySwiper.value, {
		modules: [Navigation],
		slidesPerView: 2,
		slidesPerGroup: 2,
		spaceBetween: "5.07%",
		watchOverflow: true,
		navigation: {
			prevEl: historyPrev.value,
			nextEl: historyNext.value,
			disabledClass: "is-disabled",
			lockClass: "is-locked",
		},
	});
}

function destroyHistorySwiper() {
	if (!historySwiperInstance.value) return;
	historySwiperInstance.value.destroy(true, true);
	historySwiperInstance.value = null;
}

function getHistorySlideStyle(glory) {
	if (!glory) {
		return {};
	}
	return {
		backgroundImage: `url(${glory})`,
		backgroundSize: "contain",
		backgroundPosition: "center",
		backgroundRepeat: "no-repeat",
	};
}

async function handleFocus(teamCode, status) {
	if (inRequire.value || !token.value) return;

	inRequire.value = true;
	const data = {
		seasonId: subSeasonID.value,
		teamCode,
		status: status ? 1 : 0,
	};

	try {
		const response = await api.reqFocusStatus(token.value, data);
		if (response.status === 200) {
			teamInfo.value.isFollowed = status ? 1 : 0;
			if (status) {
				teamInfo.value.followNum += 1;
			} else {
				teamInfo.value.followNum -= 1;
			}
			showPrompt(status ? "关注成功" : "取消关注成功");
		}
	} catch (err) {
		console.log(err);
	} finally {
		inRequire.value = false;
	}
}

onBeforeUnmount(() => {
	destroyHistorySwiper();
});
</script>

<style lang="scss" scoped>
@import "./clan";
</style>
